import { FC, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useWorkspace } from "../../../../context/WorkspaceContext";
import { useUserSession } from "../../../../context/UserContext";
import { getDriverRoutes, getDriverPositionByEmail } from "../../../../api/client";
import { mapDriverRoutesForMapView } from "../../../../helpers/routeMappers";
import moment from "moment";
import { OrderStatus } from "../../../../types/order/IOrder";
import { MapRoute } from "./MapRoute";
import { IMapRoute } from "../types";
import { Map } from "./Map";
import { MapControls } from "./MapControls";
import Driver from "../../../../models/Driver";
import {
  mapOverlay,
  mapOverlayHeader,
  mapOverlayToolbar,
  textHeading,
  listRowHover,
  textMuted,
} from "@design-system";

/**
 * DriverRoutesView Component
 * 
 * Architecture Decision:
 * - Driver list: Fetched from Firestore (reliable, real-time)
 * - Driver positions: Fetched from REST API when driver is selected (performance optimized)
 * 
 * This hybrid approach provides:
 * - Reliability: Driver list always loads from proven Firestore
 * - Performance: Position only fetched when needed
 * - Maintainability: Clear separation of concerns
 */

interface DriverRoutesViewProps {
  drivers: Driver[];
  selectedDate?: Date;
}

export const DriverRoutesView: FC<DriverRoutesViewProps> = ({ 
  drivers, 
  selectedDate: initialDate,
}) => {
  const { t } = useTranslation(["map"]);
  const { activeWorkspace } = useWorkspace();
  const { userSession } = useUserSession();
  const [loading, setLoading] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<string>();
  const [selectedRoute, setSelectedRoute] = useState<IMapRoute>();
  const [driverPosition, setDriverPosition] = useState<google.maps.LatLngLiteral>();
  const [noRoutesMessage, setNoRoutesMessage] = useState<string>();
  const [selectedDate, setSelectedDate] = useState<Date>(initialDate || new Date());

  const fetchDriverPosition = useCallback(async () => {
    if (!selectedDriver || !userSession?.token || !activeWorkspace?.workspaceId) {
      setDriverPosition(undefined);
      return;
    }
    try {
      const positionData = await getDriverPositionByEmail(
        userSession.token.accessToken,
        activeWorkspace.workspaceId,
        selectedDriver
      );
      
      if (positionData?.lastLocation) {
        const position = {
          lat: positionData.lastLocation.lat,
          lng: positionData.lastLocation.lon,
        };
        setDriverPosition(position);
      } else {
        setDriverPosition(undefined);
      }
    } catch (error) {
      console.error('Error fetching driver position:', error);
      setDriverPosition(undefined);
    }
  }, [selectedDriver, userSession?.token, activeWorkspace?.workspaceId]);

  useEffect(() => {
    const fetchRoutes = async () => {
      if (!selectedDriver || !userSession?.token || !activeWorkspace?.workspaceId || !selectedDate) {
        setSelectedRoute(undefined);
        setDriverPosition(undefined);
        setNoRoutesMessage(undefined);
        return;
      }
      
      setSelectedRoute(undefined);
      setDriverPosition(undefined);
      setNoRoutesMessage(undefined);
      setLoading(true);
      try {
        const formattedDate = moment(selectedDate).format('YYYY-MM-DD');
        const response = await getDriverRoutes(
          userSession.token.accessToken,
          activeWorkspace.workspaceId,
          selectedDriver,
          formattedDate
        );
        
        if (response && response.length > 0) {
          const renumberedStops = mapDriverRoutesForMapView(response);

          if (renumberedStops.length === 0) {
            showNoRoutesMessage();
            return;
          }

          // Convert to IMapRoute format
          const selectedDriverData = drivers.find(d => d.email === selectedDriver);
          const driverRoute: IMapRoute = {
            id: selectedDriver,
            customerName: Driver.getDriverName(selectedDriverData),
            type: "Driver Route",
            status: OrderStatus.InProgress,
            driver: selectedDriverData,
            driverId: selectedDriver,
            customer: {
              id: selectedDriverData?.id || "",
              name: Driver.getDriverName(selectedDriverData),
              isCompany: true,
              workspaceId: activeWorkspace?.workspaceId || ""
            },
            route: renumberedStops
          };

          setSelectedRoute(driverRoute);
          
          const fetchAndSetPosition = async () => {
            try {
              const positionData = await getDriverPositionByEmail(
                userSession.token.accessToken,
                activeWorkspace.workspaceId,
                selectedDriver
              );

              if (positionData?.lastLocation) {
                let position = {
                  lat: positionData.lastLocation.lat,
                  lng: positionData.lastLocation.lon,
                };

                const isCloseToStop = renumberedStops.some(stop => {
                  if (!stop.location.coordinate) return false;
                  const stopLat = stop.location.coordinate.lat;
                  const stopLng = stop.location.coordinate.lon;
                  const distance = Math.sqrt(
                    Math.pow(position.lat - stopLat, 2) + Math.pow(position.lng - stopLng, 2)
                  );
                  return distance < 0.001;
                });

                if (isCloseToStop) {
                  position = {
                    lat: position.lat + 0.0005,
                    lng: position.lng + 0.0005,
                  };
                }
                
                setDriverPosition(position);
              } else {
                setDriverPosition(undefined);
              }
            } catch (error) {
              console.error('Error fetching driver position:', error);
              setDriverPosition(undefined);
            }
          };

          // Fetch initial driver position
          fetchAndSetPosition();
          
          // Add a small delay before clearing loading state to ensure route is processed
          setTimeout(() => setLoading(false), 100);
        } else {
          showNoRoutesMessage()
        }
      } catch (error) {
        console.error('Error fetching driver routes:', error);
        setNoRoutesMessage('An error occurred while fetching routes. Please try again.');
        setLoading(false);
      }
    };

    fetchRoutes();
  }, [selectedDriver, selectedDate, userSession?.token, activeWorkspace?.workspaceId, drivers, t]);

  // Set up polling for driver position
  useEffect(() => {
    if (selectedDriver) {
      const intervalId = setInterval(fetchDriverPosition, 5000); // Poll every 5 seconds
      return () => clearInterval(intervalId); // Cleanup on unmount or when driver changes
    }
  }, [selectedDriver, fetchDriverPosition]);

  // Auto-select first driver if none is selected
  useEffect(() => {
    if (drivers.length > 0 && !selectedDriver) {
      setSelectedDriver(drivers[0].email);
    }
  }, [drivers, selectedDriver]);

  const showNoRoutesMessage = () => {
    const driver = drivers.find(d => d.email === selectedDriver);
    const driverName = Driver.getDriverName(driver) || 'Selected driver';
    setNoRoutesMessage(t("map:drivers.no_routes_for_date", { driverName }));
    setLoading(false);
  }

  return (
    <div className="relative h-full">
      <Map 
        selectedRoute={selectedRoute} 
        isLoading={loading}
        message={selectedDriver ? noRoutesMessage : t("map:drivers.select_driver")}
        messageTitle={t("map:drivers.dialog_title")}
        hasListOverlay={true}
      >
        <MapRoute
          selectedRoute={selectedRoute}
          driverPosition={driverPosition}
        />
      </Map>

      {/* Driver Selection Overlay */}
      <div className="absolute inset-4 w-[280px] z-10">
        <div className={mapOverlay}>
          <div className={mapOverlayHeader}>
            <h2 className={`text-base ${textHeading}`}>{t('drivers.title')}</h2>
          </div>
          
          <div className={mapOverlayToolbar}>
            <MapControls
              selectedDate={selectedDate}
              setSelectedDate={(date: Date | undefined) => date && setSelectedDate(date)}
              showStatusFilter={false}
              setSelectedTypes={() => {}}
            />
          </div>
          
          <div className="flex-1 overflow-y-auto">
            <div className="flex flex-col">
              {drivers?.length === 0 ? (
                <div className={`${textMuted} p-3 text-center text-sm`}>{t('drivers.no_drivers')}</div>
              ) : (
                drivers?.map(driver => (
                  <div 
                    key={driver.email}
                    onClick={() => setSelectedDriver(driver.email)}
                    className={`py-2 px-3 transition-colors duration-200 flex items-center gap-2 cursor-pointer border-l-2 ${
                      selectedDriver === driver.email 
                        ? 'bg-brand-muted dark:bg-brand-muted/10 border-brand' 
                        : `${listRowHover} border-transparent`
                    }`}
                  >
                    <div className="text-secondary w-7 h-7 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-medium text-sm truncate ${textHeading}`}>{Driver.getDriverName(driver)}</h3>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 